import { RACE_MAPPING, SCALE_VALUE_PRIORITY, DEFAULT_SUBJECT_ID, STORAGE_KEYS } from './constants';
import { UrlParams, UserInfo, Biomarker, Subject, Indicator, ReportInfo, ReportResponse, SurveyResponse } from './types';
import { getThemeId } from '../../components/getSubjectId';

export const parseUserInfoFromUrl = (urlParams: UrlParams, surveyDefinitions: any[] = []): UserInfo => {
  const surveyResponses = surveyDefinitions.map(def => {
    const key = def.questionKey;
    const rawValue = urlParams[key];
    if (!rawValue) return null;

    const values = String(rawValue).split(',');
    const options = def.options || [];

    const answerLabels = values.map((v: string) => {
      const opt = options.find((o: any) => o.value === v);
      return opt?.label?.en || opt?.label?.de || v;
    });

    const additionalText = urlParams[`${key}_details`] ? String(urlParams[`${key}_details`]) : undefined;

    return {
      questionKey: key,
      question: def.question?.en || def.question?.de || '',
      answer: String(rawValue),
      answerLabels,
      additionalText
    } as SurveyResponse;
  }).filter((item): item is SurveyResponse => item !== null);

  return {
    age: parseInt(String(urlParams.age || 0)) || 0,
    gender: String(urlParams.gender || 'w'),
    race: RACE_MAPPING[String(urlParams.race || 'white')] || 'caucasian',
    theme: getThemeId() || '',
    surveyResponses: surveyResponses.length > 0 ? surveyResponses : undefined
  };
};

export const parseBiomarkersFromStorage = (): Biomarker[] => {
  try {
    const biomarkersData = localStorage.getItem(STORAGE_KEYS.BIOMARKERS_DATA);
    if (biomarkersData) {
      const rawBiomarkers: any[] = JSON.parse(biomarkersData);
      
      return rawBiomarkers.map((bm: any): Biomarker => ({
        nameEn: bm.nameEn || bm.originalName || '',
        standardizedValue: typeof bm.standardizedValue === 'number' ? bm.standardizedValue : (typeof bm.originalValue === 'number' ? bm.originalValue : 0),
        standardizedUnit: bm.standardizedUnit || bm.originalUnit || '',
        interpretation: '',
        relativeScore: 0,
        labReferenceRange: {
          min: typeof bm.labReferenceRange?.min === 'number' ? bm.labReferenceRange.min : (typeof bm.labReferenceRange?.min === 'string' && bm.labReferenceRange.min !== '' ? Number(bm.labReferenceRange.min) : 0),
          max: typeof bm.labReferenceRange?.max === 'number' ? bm.labReferenceRange.max : (typeof bm.labReferenceRange?.max === 'string' && bm.labReferenceRange.max !== '' ? Number(bm.labReferenceRange.max) : 0),
          unit: bm.labReferenceRange?.unit || ''
        }
      }));
    }
  } catch (error) {
    console.error('Error parsing biomarkers data:', error);
  }
  return [];
};

export const getSubjectFromReportData = (
  reportData: ReportResponse | null,
  subjectId: number = DEFAULT_SUBJECT_ID
): Subject | null => {
  if (!reportData?.info?.subjects) return null;

  const subjectById = reportData.info.subjects.find((subject: Subject) => subject._id === subjectId);
  return subjectById || reportData.info.subjects[0];
};

export const getFilteredIndicators = (reportData: ReportResponse | null): Indicator[] => {
  if (!reportData?.info?.indicators) return [];

  const themeId = getThemeId();
  return reportData.info.indicators.filter((indicator: Indicator) => {
    const indicatorSubjectIds = indicator.subjectIds;
    if (Array.isArray(indicatorSubjectIds)) {
      return indicatorSubjectIds.includes(Number(themeId));
    }
    return Number(indicatorSubjectIds) === Number(themeId);
  });
};

export const sortIndicatorsByPriority = (indicators: Indicator[]): Indicator[] => {
  return indicators.sort((a: Indicator, b: Indicator) => {
    const scaleValueA = a.scaleValue || 'GREY';
    const scaleValueB = b.scaleValue || 'GREY';
    const priorityA = SCALE_VALUE_PRIORITY[scaleValueA] || 0;
    const priorityB = SCALE_VALUE_PRIORITY[scaleValueB] || 0;
    return priorityA - priorityB;
  });
};

export const getHealthStatusValue = (scaleValue?: string): 'optimal' | 'bad' => {
  if (scaleValue === 'G' || scaleValue === 'GL' || scaleValue === 'A') {
    return 'optimal';
  }
  return 'bad';
};

export const safeGet = (obj: any, path: string, defaultValue: any = null) => {
  try {
    return path.split('.').reduce((current, key) => current?.[key], obj) ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

export const getUploadedFileInfo = (): { fileName: string; hash: string } | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.UPLOADED_FILE_INFO);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Error parsing uploaded file info:', error);
  }
  return null;
};
